import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { showNotification } from '../utils/notifications';
import { botResponses } from '../utils/botResponses';

const initialMessages = [
  {
    type: 'bot',
    content: "👋 Hi! I'm Farhan's AI assistant. I can help you explore his portfolio, skills, and projects. How can I assist you today?",
    timestamp: new Date().toISOString()
  },
  {
    type: 'bot',
    content: "Here are some topics I can help you with:",
    options: [
      'About Farhan',
      'project demo',
      'Skills',
      'Contact Farhan'
    ],
    timestamp: new Date().toISOString()
  },
  {
    type: 'bot',
    content: "You can also ask me about:",
    options: [
      'Mern Stack',
      'Work Process',
      'how to start',
      'Availability'
    ],
    timestamp: new Date().toISOString()
  }
];

export const useChatBot = () => {
  const [messages, setMessages] = useState(() => {
    const savedMessages = localStorage.getItem('chatMessages');
    return savedMessages ? JSON.parse(savedMessages) : initialMessages;
  });
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [error, setError] = useState(null);
  const messageHistoryRef = useRef([]);
  const navigate = useNavigate();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate typing delay based on message length and device
  const getTypingDelay = useCallback((message) => {
    const baseDelay = isMobile ? 800 : 1200;
    const perCharDelay = isMobile ? 10 : 15;
    return Math.min(baseDelay + message.length * perCharDelay, 2000);
  }, [isMobile]);

  const addMessage = useCallback((content, isUser) => {
    const newMessage = {
      type: isUser ? 'user' : 'bot',
      content,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, newMessage]);
    messageHistoryRef.current.push(newMessage);

    // Save to localStorage with device-specific key
    const storageKey = `chat_history_${isMobile ? 'mobile' : 'desktop'}`;
    localStorage.setItem(storageKey, JSON.stringify(messageHistoryRef.current));
  }, [isMobile]);

  const processMessage = useCallback(async (message) => {
    try {
      setIsTyping(true);
      setError(null);

      // Handle navigation commands
      if (message.toLowerCase().startsWith('go to')) {
        const path = message.toLowerCase().replace('go to', '').trim();
        navigate(`/${path}`);
        return;
      }

      // Get bot response
      const response = botResponses[message.toLowerCase()] || botResponses.default;
      
      // Dynamic typing delay
      const typingDelay = getTypingDelay(response.content);
      await new Promise(resolve => setTimeout(resolve, typingDelay));

      addMessage(response.content, false);

      if (!isMuted) {
        showNotification({
          title: 'New Message',
          body: response.content.substring(0, isMobile ? 30 : 50) + '...',
          type: 'info',
          duration: isMobile ? 3000 : 5000
        });
      }
    } catch (err) {
      setError('Sorry I am under development. Please try again later.');
      console.error('Chat processing error:', err);
    } finally {
      setIsTyping(false);
    }
  }, [navigate, isMuted, isMobile, getTypingDelay]);

  const clearChat = useCallback(() => {
    setMessages(initialMessages);
    setUnreadCount(0);
    messageHistoryRef.current = [];
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  useEffect(() => {
    // Save chat history to localStorage
    const saveHistory = () => {
      localStorage.setItem('chatHistory', JSON.stringify(messageHistoryRef.current));
    };
    window.addEventListener('beforeunload', saveHistory);
    return () => window.removeEventListener('beforeunload', saveHistory);
  }, []);

  return {
    messages,
    isTyping,
    unreadCount,
    isMuted,
    isMobile,
    error,
    processMessage,
    addMessage,
    clearChat,
    toggleMute,
    setUnreadCount
  };
};

export default useChatBot;